import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ThemeMode } from '../theme/contract';
import { defaultThemeIdForPersona, isThemeId } from '../theme/registry';

export const THEME_STORAGE_KEY = 'kidshome.theme';

/** What we persist + the active resolved values. */
export interface ThemeState {
  /**
   * The user's chosen / persona-default theme id (always a registered id). This is the persisted
   * preference — what the UI returns to when no surface-scoped theme is active.
   */
  themeId: string;
  /**
   * A NON-PERSISTING, surface-scoped theme override (e.g. the admin surface forces `command-center`
   * while mounted). When set it wins over {@link themeId} for the active theme, but it is never
   * written to localStorage and never sets {@link explicit} — leaving the surface restores the user's
   * `themeId`. `null` when no surface override is active.
   */
  scopedThemeId: string | null;
  /** Active mode. `'system'` follows the OS preference; otherwise an explicit choice. */
  mode: ThemeMode | 'system';
  /**
   * A NON-PERSISTING, surface-scoped MODE override (e.g. the kid persona is light-first by design and
   * pins `'light'` while mounted, so its always-light bespoke surfaces never clash with an OS-dark
   * `--color-*` token set). When set it wins over {@link mode} for the active mode — and, being a
   * concrete value, it also silences the `'system'` OS-preference listener — but it is never persisted;
   * leaving the surface restores the user's `mode`. `null` when no surface override is active.
   */
  scopedMode: ThemeMode | null;
  /**
   * True once the user has explicitly picked a theme. Until then the theme tracks the persona
   * default; after an explicit pick we keep their choice even across persona changes.
   */
  explicit: boolean;
  setTheme: (themeId: string) => void;
  setMode: (mode: ThemeMode | 'system') => void;
  /** Apply the persona default unless the user already made an explicit choice. */
  applyPersonaDefault: (persona: 'kid' | 'family' | 'admin') => void;
  /**
   * Apply a surface-scoped theme that does NOT persist and does NOT mark an explicit choice — used by
   * a surface (e.g. admin) that should always render in a specific theme while mounted. Pass the
   * theme id (typically derived from a persona via `defaultThemeIdForPersona`), never a hardcoded
   * literal in a component.
   */
  applyScopedTheme: (themeId: string) => void;
  /** Clear the surface-scoped override, restoring the user's persisted/persona `themeId`. */
  clearScopedTheme: () => void;
  /**
   * Apply a surface-scoped MODE that does NOT persist — used by a surface that must always render in a
   * specific mode while mounted (e.g. the always-light kid persona). Restored on {@link clearScopedMode}.
   */
  applyScopedMode: (mode: ThemeMode) => void;
  /** Clear the surface-scoped mode override, restoring the user's `mode`. */
  clearScopedMode: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      themeId: defaultThemeIdForPersona('family'),
      scopedThemeId: null,
      mode: 'system',
      scopedMode: null,
      explicit: false,
      setTheme: (themeId) => {
        if (!isThemeId(themeId)) {
          return;
        }
        set({ themeId, explicit: true });
      },
      setMode: (mode) => set({ mode }),
      applyPersonaDefault: (persona) => {
        if (get().explicit) {
          return;
        }
        set({ themeId: defaultThemeIdForPersona(persona) });
      },
      applyScopedTheme: (themeId) => {
        if (!isThemeId(themeId)) {
          return;
        }
        set({ scopedThemeId: themeId });
      },
      clearScopedTheme: () => set({ scopedThemeId: null }),
      applyScopedMode: (mode) => set({ scopedMode: mode }),
      clearScopedMode: () => set({ scopedMode: null }),
    }),
    {
      name: THEME_STORAGE_KEY,
      // Note: `scopedThemeId` is intentionally NOT persisted — a surface override is ephemeral.
      partialize: (state) => ({
        themeId: state.themeId,
        mode: state.mode,
        explicit: state.explicit,
      }),
    },
  ),
);

/** The active theme id: a surface-scoped override wins over the user's persisted/persona theme. */
export function activeThemeId(state: Pick<ThemeState, 'themeId' | 'scopedThemeId'>): string {
  return state.scopedThemeId ?? state.themeId;
}

/**
 * The active mode preference: a surface-scoped override wins over the user's `mode`. A scoped value is
 * always concrete (`'light'`/`'dark'`), so selecting through this also disables the `'system'` listener.
 */
export function activeMode(state: Pick<ThemeState, 'mode' | 'scopedMode'>): ThemeMode | 'system' {
  return state.scopedMode ?? state.mode;
}

/** Resolve `'system'` to a concrete mode using the media query (SSR-safe: defaults to light). */
export function resolveMode(mode: ThemeMode | 'system'): ThemeMode {
  if (mode !== 'system') {
    return mode;
  }
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
